import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  Checkbox,
  Modal,
  Divider,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  ShoppingCart as ShoppingCartIcon,
  GetApp as DownloadIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalFireDepartment as FlameIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

// Define interface for the product data structure based on the application's existing types
interface ProductImage {
  image_url: string;
  alt_text?: string;
  is_primary: boolean;
  display_order: number;
  id: string;
  product_id: string;
}

interface ImageDisplay {
  image_url: string;
  alt_text?: string;
}

interface ProductWithImages {
  id: string;
  sku: string;
  name: string;
  price: number;
  is_active: boolean;
  description?: string;
  category?: string;
  images?: ProductImage[];
  specifications?: Record<string, any>;
  manual?: string;
  model?: string;
  reseller_commission_rate?: number;
}

interface ProductDetailsModalProps {
  open: boolean;
  onClose: () => void;
  product: ProductWithImages;
  onAddToCart?: (product: ProductWithImages, quantity: number) => void;
}

export default function ProductDetailsModal({
  open,
  onClose,
  product,
  onAddToCart,
}: ProductDetailsModalProps) {
  const [isFavorite, setIsFavorite] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Apply styles to iframes in description content
  useEffect(() => {
    if (open && product.description) {
      const iframes = document.querySelectorAll('.product-description-container iframe');
      iframes.forEach(iframe => {
        if (iframe instanceof HTMLIFrameElement) {
          iframe.style.height = '600px'; // Fixed height to ensure visibility
          iframe.style.width = '100%';
          iframe.style.border = 'none';
          iframe.style.display = 'block';
          iframe.style.marginBottom = '10px';
        }
      });
    }
  }, [open, product.description]);

  // Parse specifications or use empty object if not available
  let specifications: Record<string, any> = {};
  if (product.specifications) {
    specifications = product.specifications;
  }

  // Format specification values for better display
  const formatSpecValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return 'N/A';
    }

    if (typeof value === 'object') {
      if (Array.isArray(value)) {
        return value.join(', ');
      } else {
        // Return nested object renderer instead of JSON string
        return (
          <Box sx={{ pl: 2, mt: 1 }}>
            {Object.entries(value).map(([nestedKey, nestedValue]) => (
              <Box key={nestedKey} sx={{ mb: 1 }}>
                <Typography variant="body2" fontWeight={500} component="span">
                  {nestedKey}:{' '}
                </Typography>
                <Typography variant="body2" component="span">
                  {typeof nestedValue === 'object'
                    ? formatSpecValue(nestedValue)
                    : String(nestedValue)}
                </Typography>
              </Box>
            ))}
          </Box>
        );
      }
    }

    return String(value);
  };

  // Function to get product images
  const getProductImages = (): ImageDisplay[] => {
    if (product.images && product.images.length > 0) {
      return product.images
        .sort((a, b) => a.display_order - b.display_order)
        .map(img => ({
          image_url: img.image_url,
          alt_text: img.alt_text,
        }));
    }
    return [{ image_url: '/placeholder.svg?height=300&width=400', alt_text: 'Placeholder' }];
  };

  const images = getProductImages();

  // Functions for image carousel
  const goToNextImage = () => {
    setCurrentImageIndex((currentImageIndex + 1) % images.length);
  };

  const goToPrevImage = () => {
    setCurrentImageIndex((currentImageIndex - 1 + images.length) % images.length);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="product-details-modal-title"
      aria-describedby="product-details-modal-description"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          position: 'relative',
          maxHeight: { xs: '100vh', sm: '85vh' },
          width: '100%',
          maxWidth: { xs: '100%', sm: '5xl', lg: '1152px' },
          overflow: 'hidden',
          bgcolor: 'white',
          p: { xs: '12px', sm: '20px 25px' },
          boxShadow: 24,
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: '#1c1b1f',
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box sx={{ flex: '1 1' }}>
              <Typography variant="h4" component="h2" color="#1c1b1f" sx={{ fontWeight: 500 }}>
                {product.name}
              </Typography>
              <Typography variant="body1" color="#2b3445" sx={{ mb: 1 }}>
                R${' '}
                {product.price.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: '0 1',
                textAlign: { xs: 'left', sm: 'right' },
                marginRight: { xs: '0', sm: '40px' },
              }}
            >
              <Typography
                variant="body2"
                color="#008001"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: '200px',
                  justifyContent: { xs: 'flex-start', sm: 'flex-end' },
                }}
              >
                <FlameIcon sx={{ fontSize: 16 }} />
                Comissão Inclusa {((product.reseller_commission_rate || 0.08) * 100).toFixed(0)}%
              </Typography>
              <Typography variant="body2" color="#444444" sx={{ mt: 1 }}>
                Preço para cliente final | UF SP
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'auto',
            overflow: 'auto',
            maxHeight: 'calc(75vh - 120px)',
            paddingRight: '5px',
          }}
        >
          {/* Images Section */}
          <Box
            sx={{
              width: '100%',
              mb: 4,
            }}
          >
            {/* Image Carousel */}
            <Box
              sx={{
                mb: 4,
                p: 4,
                border: '1px solid #d9d9d9',
                borderRadius: 1,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                }}
              >
                <img
                  src={images[currentImageIndex].image_url}
                  alt={images[currentImageIndex].alt_text || product.name}
                  style={{ maxWidth: '100%', height: 'auto' }}
                />

                {images.length > 1 && (
                  <>
                    <IconButton
                      onClick={goToPrevImage}
                      sx={{
                        position: 'absolute',
                        left: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(255,255,255,0.7)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      }}
                    >
                      <ArrowBackIcon />
                    </IconButton>
                    <IconButton
                      onClick={goToNextImage}
                      sx={{
                        position: 'absolute',
                        right: 0,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        bgcolor: 'rgba(255,255,255,0.7)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                      }}
                    >
                      <ArrowForwardIcon />
                    </IconButton>
                  </>
                )}
              </Box>

              {/* Thumbnail navigation */}
              {images.length > 1 && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    gap: 1,
                    flexWrap: 'wrap',
                  }}
                >
                  {images.map((img, index) => (
                    <Box
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      sx={{
                        width: 60,
                        height: 60,
                        border:
                          index === currentImageIndex ? '2px solid #1082ff' : '1px solid #d9d9d9',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                      }}
                    >
                      <img
                        src={img.image_url}
                        alt={img.alt_text || `Thumbnail ${index + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* Price Information Section */}
          <Box
            sx={{
              width: '100%',
              mb: 4,
            }}
          >
            <Box sx={{ border: '1px solid #d9d9d9', borderRadius: 1, p: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 4,
                }}
              >
                <Box
                  sx={{
                    border: '1px solid #008001',
                    bgcolor: '#f6ffed',
                    px: 3,
                    py: 1,
                    borderRadius: 1,
                    color: '#008001',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <FlameIcon sx={{ fontSize: 16 }} />
                  <Typography>
                    Comissão Inclusa {((product.reseller_commission_rate || 0.08) * 100).toFixed(0)}
                    %
                  </Typography>
                </Box>
                <IconButton
                  onClick={() => setIsFavorite(!isFavorite)}
                  sx={{ color: isFavorite ? '#ff0000' : '#9e9e9e' }}
                >
                  {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
              </Box>

              <Box
                sx={{
                  bgcolor: '#1c1b1f',
                  py: 2,
                  textAlign: 'center',
                  color: 'white',
                  mb: 4,
                }}
              >
                <Typography>Preço para cliente final | UF SP</Typography>
              </Box>

              <Typography variant="body2" color="#444444" sx={{ mb: 2 }}>
                SKU: {product.sku}
              </Typography>

              <Typography
                variant="h4"
                component="h3"
                fontWeight={500}
                color="#1c1b1f"
                sx={{ mb: 2 }}
              >
                {product.name}
              </Typography>

              <Typography variant="h4" color="#1e1e1e" sx={{ fontWeight: 700, mb: 1 }}>
                R${' '}
                {product.price.toLocaleString('pt-BR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>

              <Box sx={{ mb: 4 }}>
                <Box
                  component="span"
                  sx={{
                    display: 'inline-block',
                    border: '1px solid #d9d9d9',
                    borderRadius: 1,
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                    color: '#444444',
                  }}
                >
                  Impostos + Frete Inclusos
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (onAddToCart) {
                      onAddToCart(product, 1);
                    }
                    onClose();
                  }}
                  startIcon={<ShoppingCartIcon />}
                  sx={{
                    bgcolor: '#80c343',
                    color: 'white',
                    fontWeight: 500,
                    borderRadius: 1,
                    p: '10px 16px',
                    textTransform: 'none',
                    width: '100%',
                    '&:hover': {
                      bgcolor: '#72b03c',
                    },
                  }}
                >
                  Ir para cotação
                </Button>
              </Box>
            </Box>
          </Box>

          {/* Manual Download Section */}
          <Box
            sx={{
              width: '100%',
              mb: 4,
            }}
          >
            <Box sx={{ p: 4, border: '1px solid #d9d9d9', borderRadius: 1 }}>
              <Button
                startIcon={<DownloadIcon />}
                component={product.manual ? 'a' : 'button'}
                href={product.manual || undefined}
                target="_blank"
                rel="noopener noreferrer"
                disabled={!product.manual}
                sx={{
                  color: product.manual ? '#1082ff' : '#9e9e9e',
                  textTransform: 'none',
                  width: '100%',
                  justifyContent: 'center',
                  textDecoration: 'none',
                }}
              >
                {product.manual
                  ? 'Fazer download da ficha técnica'
                  : 'Ficha técnica não disponível'}
              </Button>
            </Box>
          </Box>

          {/* Specifications Section */}
          <Box
            sx={{
              width: '100%',
              mb: 4,
            }}
          >
            <Box sx={{ border: '1px solid #d9d9d9', borderRadius: 1 }}>
              {/* Map through specifications object */}
              {Object.entries(specifications).map(([key, value], index) => (
                <Box
                  key={key}
                  sx={{
                    borderBottom:
                      index < Object.keys(specifications).length - 1 ? '1px solid #d9d9d9' : 'none',
                    p: 4,
                  }}
                >
                  <Typography variant="body1" fontWeight={500}>
                    {key}:
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      color: '#444444',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatSpecValue(value)}
                  </Box>
                </Box>
              ))}

              {/* If no specifications, show a message */}
              {Object.keys(specifications).length === 0 && (
                <Box sx={{ p: 4 }}>
                  <Typography color="#444444">
                    Detalhes das especificações não disponíveis.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Description Section - directly render the iframe content */}
          {product.description && (
            <div
              className="product-description-container"
              dangerouslySetInnerHTML={{ __html: product.description }}
              style={{
                width: '100%',
                height: '600px',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                display: 'block',
                marginBottom: '20px',
              }}
            />
          )}
        </Box>
      </Card>
    </Modal>
  );
}
